// Copyright (c) StrangeBee 2024

import React from 'react';
import { ExtractProps } from '@Molecules/Details/Details';
import {
    CaseContributor,
    CaseOutput,
    UserOutput,
    CustomFieldOutput,
    CaseStatusStage,
    CustomStatusMapType,
    PermissionsEnum,
} from 'thehive-sdk';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { Avatar, FormInstance } from 'antd';
import { THForm } from '@Molecules/THForm/THForm';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { CaseDrawerCount } from '@Organisms/Case/CaseDrawerCount/CaseDrawerCount';
import { SecurityTagList } from '@Molecules/SecurityTagList/SecurityTagList';
import { CFList } from '@Molecules/CFList/CFList';
import { normalizeDate } from '@Helpers/date';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { Select } from 'antd';
import { convertToLocaleId } from '@Helpers/locale';
import { THUserSelector } from '@Molecules/Inputs/THUserSelector/THUserSelector';
import { onCaseImageUpload } from '@Helpers/download';
import { PermissionExtract } from '@Hooks/useSplitPermissions';

import './extractData.scss';

const { OptGroup } = Select;

interface ExtractDataProps {
    data: CaseOutput;
    hasPermissions: Record<PermissionExtract<[PermissionsEnum.MANAGE_CASE_UPDATE]>, boolean>;
    locale: (label: string | any) => string;
    formEditMode?: boolean;
}

export const extractData = ({
    data: { _id: caseId, stage },
    hasPermissions,
    locale,
    formEditMode,
}: ExtractDataProps): Record<any, ExtractProps> => ({
    '#computeSecurity': {
        className: 'case-drawer__security',
        isForce: false,
        render: () => <SecurityTagList hasPermissions={hasPermissions?.updateCase} />,
    },
    '#computeAssigneeContributors': {
        className: 'case-drawer__assignee',
        isForce: true,
        render: ({
            assignableUsers,
            refetchAssignableUsers,
            contributors,
        }: {
            assignableUsers?: UserOutput[];
            refetchAssignableUsers: () => void;
            contributors: CaseContributor[];
        }) => (
            <div className="case-drawer__block">
                <THForm.Item name="assignee" label={locale('common.assignee')}>
                    <THUserSelector
                        allowClear={false}
                        placeholder={locale('general.unassigned')}
                        users={assignableUsers}
                        disabled={!hasPermissions?.updateCase}
                        onDropdownVisibleChange={(open) => open && refetchAssignableUsers()}
                    />
                </THForm.Item>

                <THForm.Item label={locale('common.contributors')}>
                    <Avatar.Group size="large">
                        {contributors.map(({ login }) => (
                            <AssigneeInfo key={login} assignee={login} avatarOnly={true} placement="top" />
                        ))}
                    </Avatar.Group>
                </THForm.Item>
            </div>
        ),
    },
    startDate: {
        isForce: true,
        className: 'case-drawer__contributors',
        render: () => (
            <div className="case-drawer__block">
                <THForm.Item name="startDate" label={locale('common.start-date')} normalize={normalizeDate}>
                    <THDatePicker
                        borderless
                        disabled={!hasPermissions?.updateCase}
                        placeholder={locale('general.none')}
                    />
                </THForm.Item>
                <THForm.Item name="endDate" label={locale('common.end-date')}>
                    <THDatePicker borderless disabled placeholder={locale('general.none')} />
                </THForm.Item>
            </div>
        ),
    },
    '#computeCount': {
        isForce: false,
        className: 'case-drawer__count',
        render: ({
            taskStats: { total: taskCount = 0 },
            observableStats: { total: obsCount = 0 },
            procedureCount = 0,
            permissions,
            onEditCounter,
        }: CaseOutput['extraData'] & { onEditCounter?: () => void }) => (
            <CaseDrawerCount
                permissions={permissions}
                taskCount={taskCount}
                obsCount={obsCount}
                procedureCount={procedureCount}
                caseId={caseId}
                hasPermissions={hasPermissions?.updateCase}
                onEditCounter={onEditCounter}
            />
        ),
    },
    title: {
        className: 'case-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item
                name="title"
                label={locale('common.title')}
                rules={[
                    {
                        required: hasPermissions?.updateCase,
                        message: locale('app.general.required'),
                    },
                ]}
            >
                <THInput borderless disabled={!hasPermissions?.updateCase} />
            </THForm.Item>
        ),
    },
    '#computeStatus': {
        className: 'case-drawer__section',
        isForce: false,
        render: ({
            statusList,
            isOpen = false,
            impactStatus = 'NoImpact',
        }: {
            statusList: CustomStatusMapType;
            isOpen: boolean;
            impactStatus: string;
        }) => (
            <div className="case-drawer__status-impact">
                <THForm.Item name="status" label={locale('common.status')}>
                    <THSelect borderless disabled={!isOpen || !hasPermissions?.updateCase}>
                        {Object.entries(statusList).map(([key, values]) => (
                            <OptGroup key={key} label={key}>
                                {values.map(({ value, colour }) => (
                                    <THSelect.Option key={value} value={value}>
                                        <CustomColorLabel
                                            value={locale({
                                                id: `case.data.status.${convertToLocaleId(value)}`,
                                                defaultMessage: value,
                                            })}
                                            color={colour}
                                        />
                                    </THSelect.Option>
                                ))}
                            </OptGroup>
                        ))}
                    </THSelect>
                </THForm.Item>
                {!isOpen && (
                    <THForm.Item name="impactStatus" label={locale('case.close.form.impactStatus.label')}>
                        <span className="status-impact__impact-label">
                            {
                                {
                                    WithImpact: locale('general.yes'),
                                    NoImpact: locale('general.no'),
                                }[impactStatus]
                            }
                        </span>
                    </THForm.Item>
                )}
            </div>
        ),
    },
    tags: {
        className: 'case-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="tags" label={locale('common.tags')}>
                <THTagSelector.Autocomplete
                    borderless
                    disabled={!hasPermissions?.updateCase}
                    placeholder={locale('common.tags')}
                />
            </THForm.Item>
        ),
    },
    description: {
        className: 'case-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="description" label={locale('task.details.description')}>
                <THMarkdown
                    borderless
                    disabled={!hasPermissions?.updateCase}
                    onImageUpload={onCaseImageUpload(caseId)}
                    formEditMode={formEditMode}
                />
            </THForm.Item>
        ),
    },
    summary: {
        className: 'case-drawer__resolution-status',
        isForce: false,
        render: () =>
            stage === CaseStatusStage.CLOSED ? (
                <THForm.Item
                    name="summary"
                    label={locale('common.summary')}
                    className="case-drawer__resolution-status__summary"
                >
                    <THMarkdown
                        borderless
                        disabled={!hasPermissions?.updateCase}
                        onImageUpload={onCaseImageUpload(caseId)}
                    />
                </THForm.Item>
            ) : null,
    },
    '#computeCustomFields': {
        className: 'case-drawer__section',
        isForce: true,
        render: ({
            customFields,
            form,
            activeKeyState,
        }: {
            customFields: CustomFieldOutput[];
            form: FormInstance;
            activeKeyState: {
                activeKey: string;
                setActiveKey: React.Dispatch<React.SetStateAction<string>>;
            };
        }) => (
            <CFList
                form={form}
                customFields={customFields}
                disabled={!hasPermissions?.updateCase}
                activeKeyState={activeKeyState}
            />
        ),
    },
});
